import { FaTicket } from "react-icons/fa6";
import {
  FiClock,
  FiCreditCard,
  FiEye,
  FiMail,
  FiMessageCircle,
  FiUser,
  FiZap,
} from "react-icons/fi";
import type { IconType } from "react-icons";
import type { ReactNode } from "react";
import { LinkProps } from "react-router";
import { PrimaryButton } from "~/components/Button";
import { Text } from "~/components/Text";
import { EventFragment, EventType } from "~/__generated__/graphql/graphql";
import { centsToMoney } from "~/utils/money";
import { getActionCopy } from "./UrgencyPanel";
import { useRootLocale } from "~/root";
import {
  formatOnlyPlacesLeft,
  getRegionalCopy,
} from "~/utils/localization";
import { getDisplayedTicketPriceCents } from "./ticketVariations";

type BottomCTAProps = {
  plan: EventFragment;
  hasBooking?: boolean;
  isAttending: boolean;
  atCapacity: boolean;
  capacity: number;
  attendeeCount: number;
  waitlistSize: number;
  to: LinkProps["to"];
  onClick?: () => void;
  onSeeOtherPlans?: () => void;
  ticketVariationsEnabled?: boolean;
};

type Feature = { label: string; Icon: IconType };


const FREE_FEATURES: Feature[] = [
  { label: "Get into the group chat", Icon: FiMessageCircle },
  { label: "DM the planner directly", Icon: FiUser },
  { label: "See who's going", Icon: FiEye },
];

export function BottomCTA({
  plan,
  isAttending,
  atCapacity,
  capacity,
  attendeeCount,
  waitlistSize,
  to,
  onClick,
  onSeeOtherPlans,
  ticketVariationsEnabled = false,
  hasBooking = false,
}: BottomCTAProps) {
  const locale = useRootLocale();
  const copy = getActionCopy({
    plan,
    isAttending,
    atCapacity,
    locale,
    ticketVariationsEnabled,
    hasBooking,
  });
  const regionalCopy = getRegionalCopy(locale);

  if (!hasBooking && (plan.canceled || plan.hasEnded || isAttending)) return null;

  const isTicketed = plan.type === EventType.Ticketed;
  const priceCents = getDisplayedTicketPriceCents(plan, ticketVariationsEnabled);
  const price = priceCents === undefined ? null : centsToMoney(priceCents, "$", locale);

  const seeOtherPlans = copy.mode === "see-other-plans";
  const isWaitlist = copy.mode === "waitlist";
  const isPendingApproval = copy.mode === "pending-approval";
  const isRequestStatus = copy.mode === "request-status";
  const isScheduled = copy.mode === "scheduled";

  const effectiveTo = seeOtherPlans ? undefined : to;
  const effectiveOnClick = seeOtherPlans ? onSeeOtherPlans : onClick;

  let title: string;
  let subtitle: string | null;
  let pill: ReactNode = null;
  let showFeatures: boolean;
  let showRefundLink: boolean;

  if (hasBooking) {
    title = `Don’t miss ${plan.title}`;
    subtitle = null;
    showFeatures = true;
    showRefundLink = isTicketed;
  } else if (isPendingApproval || isRequestStatus) {
    title = copy.label;
    subtitle = copy.sublabel;
    showFeatures = false;
    showRefundLink = false;
  } else if (isScheduled) {
    title = `${plan.title} is launching soon`;
    subtitle = "Tickets are going live soon.";
    showFeatures = false;
    showRefundLink = false;
  } else if (isWaitlist) {
    title = `Missed out on ${plan.title}?`;
    subtitle =
      `Join the waitlist - we'll let you know the moment a ${regionalCopy.place} becomes available.`;
    showFeatures = false;
    showRefundLink = false;
    if (waitlistSize > 0) {
      pill = (
        <span className="inline-flex items-center justify-center rounded-full bg-butter-cream-light/40 px-4 py-1.5 font-sans text-sm font-semibold text-butter-cream-light tracking-[-0.14px]">
          🔥{" "}
          {waitlistSize === 1
            ? "1 person waitlisted"
            : `${waitlistSize} people waitlisted`}
        </span>
      );
    }
  } else if (seeOtherPlans) {
    title = isTicketed
      ? `${plan.title} sold out fast`
      : `${plan.title} filled up fast`;
    subtitle =
      "Find your next plan on Butter - there's over 300 happening every month.";
    showFeatures = false;
    showRefundLink = false;
  } else {
    title = `Don't miss ${plan.title}`;
    subtitle = isTicketed
      ? null
      : "Everyone turns up solo — that's kind of the point.";
    showFeatures = true;
    showRefundLink = isTicketed;

    const placesLeft = Math.max(0, capacity - attendeeCount);
    const showSpotsLeftPill = !atCapacity && placesLeft > 0 && placesLeft <= 5;
    if (showSpotsLeftPill) {
      pill = (
        <span className="inline-flex items-center justify-center rounded-full bg-butter-cream-light/40 px-4 py-1.5 font-sans text-sm font-semibold text-butter-cream-light tracking-[-0.14px]">
          🔥 {formatOnlyPlacesLeft(placesLeft, locale)}
        </span>
      );
    }
  }

  const ctaLabel =
    !isPendingApproval &&
    !isRequestStatus &&
    !isScheduled &&
    !isWaitlist &&
    !seeOtherPlans &&
    isTicketed &&
    price
      ? `${copy.ctaLabel} · from ${price}`
      : copy.ctaLabel;

  const renderIcon = () => {
    switch (copy.icon) {
      case "ticket":
        return <FaTicket size={18} aria-hidden />;
      case "mail":
        return <FiMail size={18} aria-hidden />;
      case "clock":
        return <FiClock size={18} aria-hidden />;
      case "none":
        return undefined;
    }
  };

  const features: Feature[] = isTicketed
    ? [
        plan.approveGuests
          ? { label: "See who is going", Icon: FiEye }
          : { label: "No account required.", Icon: FiUser },
        { label: "Checkout as a guest", Icon: FiCreditCard },
        { label: "No app needed.", Icon: FiZap },
      ]
    : FREE_FEATURES;

  return (
    <section className="relative mt-12 overflow-hidden rounded-xl bg-butter-pink px-6 py-[52px] text-center sm:px-10 sm:py-[60px] lg:mt-24">
      <div
        aria-hidden
        className="pointer-events-none absolute -right-20 -top-20 h-80 w-80 rounded-full bg-white/40 blur-3xl"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -bottom-16 -left-16 h-64 w-64 rounded-full bg-butter-lime/40 blur-3xl"
      />

      <div className="relative mx-auto flex max-w-xl flex-col items-center gap-6">
        {pill}
        <div className="flex flex-col gap-2">
          <Text
            as="h2"
            variant="primary"
            size="lg"
            color="light"
            className="!text-[32px] !leading-[1.25] sm:!text-[34px]"
          >
            {title}
          </Text>
          {subtitle && (
            <Text
              as="p"
              variant="secondary"
              size="md"
              color="light"
              className="text-center"
            >
              {subtitle}
            </Text>
          )}
        </div>

        {showFeatures && (
          <div className="grid w-full grid-cols-1 divide-y divide-butter-cream-light/25 py-3 sm:grid-cols-3 sm:divide-x sm:divide-y-0">
            {features.map(({ label, Icon }) => (
              <div
                key={label}
                className="flex flex-row items-center justify-center gap-2 px-2 py-2.5 text-butter-cream-light sm:flex-col sm:gap-1.5 sm:py-0"
              >
                <Icon size={18} aria-hidden className="opacity-80" />
                <Text
                  as="span"
                  variant="secondary"
                  size="md"
                  color="light"
                  weight="regular"
                  className="text-center"
                >
                  {label}
                </Text>
              </div>
            ))}
          </div>
        )}

        {effectiveOnClick && !copy.disabled ? (
          <PrimaryButton
            theme="cream-light"
            onClick={effectiveOnClick}
            className="!w-auto whitespace-nowrap !px-8 !py-3.5 text-base font-bold !text-butter-pink"
            leftIcon={renderIcon()}
          >
            {ctaLabel}
          </PrimaryButton>
        ) : (
          <PrimaryButton
            theme="cream-light"
            to={copy.disabled ? undefined : effectiveTo}
            disabled={copy.disabled}
            className="!w-auto whitespace-nowrap !px-8 !py-3.5 text-base font-bold !text-butter-pink"
            leftIcon={renderIcon()}
          >
            {ctaLabel}
          </PrimaryButton>
        )}

        {showRefundLink && (
          <Text
            as="p"
            variant="secondary"
            size="sm"
            color="light"
            className="opacity-70"
          >
            View our{" "}
            <a
              href="https://www.joinbutter.co/refund-policy"
              target="_blank"
              rel="noreferrer"
              className="underline hover:opacity-80"
            >
              Refund &amp; Cancellation policy
            </a>
          </Text>
        )}
      </div>
    </section>
  );
}
